import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { handleErrorToast } from 'src/components/molecules';
import { ActiveCategory } from 'src/components/organisms/ActiveCategoriesSection/ActiveCategoryCard';
import { transformSpeakUpCategories } from 'src/components/pages/FormCreation/components/Configuration/helpers/categoryDataTransformers';
import { isSameCategoryName } from 'src/components/pages/FormCreation/components/Configuration/helpers/isSameCategoryName';
import type { DuplicateFromState } from 'src/components/pages/FormCreation/FormCreation';
import { getSlugFromFormType } from 'src/components/pages/FormCreation/helpers/formTypeMapper';
import {
  CategoryType,
  useFormManagementQuestionnairesQuery,
} from 'src/graphql';
import { TRootState } from 'src/store';
import { buildAuditLogCategoryIdMap } from './auditLogCategoryMap';
import {
  currentPageUpdated,
  filtersReset,
  formsUpdated,
  isLoadingUpdated,
  itemsPerPageUpdated,
  sortUpdated,
} from './CoiFormManagement.slice';
import {
  getFormManagementColumnWidths,
  resolveFormManagementTableConfig,
} from './formManagementTableConfig';
import {
  buildFormManagementTableData,
  formatFormManagementYmd,
} from './formManagementTableData';
import { filterForms, hasActiveFilters } from './formTableFilters';
import { sortForms } from './formTableSort';
import {
  getPublishedCoiForms,
  getPublishedSpeakUpForms,
} from './groupFormsByCategory';
import { mapQuestionnairesToFormData } from './mapQuestionnaireToFormData';
import { FormData, FormType, type FormManagementSection } from './types';
import { useFormManagementCategoryEligibility } from './useFormManagementCategoryEligibility';
import {
  FORM_TYPE_ID_TO_URL_SLUG,
  useFormManagementFeatureFlags,
} from './useFormManagementFeatureFlags';
import { useFormManagementRowMutations } from './useFormManagementRowMutations';
import { getFormManagementUrlWithTab } from './utils/getFormManagementUrl';

const DUPLICATE_MODE = {
  ALL_LANGUAGES: 'all-languages' as const,
  DEFAULT_ONLY: 'default-only' as const,
};

export function useCoiFormManagementPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditLogForm, setAuditLogForm] = useState<FormData | null>(null);
  const [formToDuplicate, setFormToDuplicate] = useState<FormData | null>(null);
  const [noCategoriesBannerDismissed, setNoCategoriesBannerDismissed] =
    useState(false);
  const [selectedProductTab, setSelectedProductTab] =
    useState<FormManagementSection>('SPEAK_UP');

  const flags = useFormManagementFeatureFlags();
  const {
    forms,
    selectedTab,
    currentPage,
    itemsPerPage,
    searchQuery,
    selectedStatuses,
    sortColumnLabel,
    sortDirection,
  } = useSelector((state: TRootState) => state.coiFormManagement);

  const {
    data: questionnairesData,
    isLoading,
    isFetching: isTableFetching,
    isError,
    refetch,
  } = useFormManagementQuestionnairesQuery(
    { filters: undefined },
    { refetchOnWindowFocus: false },
  );

  const isTableLoading = isTableFetching ?? isLoading;

  const { data: categoriesQuestionnairesData, isLoading: isCategoriesLoading } =
    useFormManagementQuestionnairesQuery(
      { filters: undefined },
      { refetchOnWindowFocus: false },
    );

  const {
    organisationCategoriesData,
    coiOrganisationCategoriesData,
    canCreateForm,
    isCategoriesUnsettledForEnabledProducts,
    hasCategoriesForAnyEnabledProduct,
    createFormDisabledDueToCategories,
    showCreateFormDropdown,
    showNoCategoriesBanner,
    refetchSelectedTabCategories,
  } = useFormManagementCategoryEligibility({
    speakUpEnabled: flags.speakUpEnabled,
    coiEnabled: flags.coiEnabled,
    selectedProductTab,
    isTableLoading,
    noCategoriesBannerDismissed,
  });

  const hasPageError = isError;
  const refetchPageData = useCallback(() => {
    refetch();
    refetchSelectedTabCategories();
  }, [refetch, refetchSelectedTabCategories]);

  const {
    formToArchive,
    setFormToArchive,
    handleArchive,
    handleArchiveConfirm,
    formToUnpublish,
    setFormToUnpublish,
    handleUnpublish,
    handleUnpublishConfirm,
    formToDelete,
    setFormToDelete,
    handleDelete,
    handleDeleteConfirm,
    deleteMutationLoading,
    unpublishMutationLoading,
  } = useFormManagementRowMutations({
    refetchForms: refetch,
    refetchFormsAndCategories: refetchPageData,
  });

  useEffect(() => {
    dispatch(isLoadingUpdated(isLoading));
  }, [isLoading, dispatch]);

  useEffect(() => {
    if (!flags.speakUpEnabled && flags.coiEnabled) {
      setSelectedProductTab('COI');
    } else if (flags.speakUpEnabled && !flags.coiEnabled) {
      setSelectedProductTab('SPEAK_UP');
    }
  }, [flags.speakUpEnabled, flags.coiEnabled]);

  useEffect(() => {
    if (!questionnairesData?.formManagementQuestionnaires) return;
    if (isTableFetching) return;
    const items = questionnairesData.formManagementQuestionnaires;
    try {
      const mapped = mapQuestionnairesToFormData(items);
      dispatch(formsUpdated(mapped));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown mapping error';
      console.error('[Forms] Failed to map questionnaires', {
        errorMessage,
        itemCount: items.length,
      });
      handleErrorToast({
        toastMessage: 'Unable to load forms',
        toastDescription:
          'Something went wrong while preparing the form list. Please try again.',
      });
      dispatch(formsUpdated([]));
    }
  }, [questionnairesData, dispatch, isTableFetching]);

  const formsByEnabledTypes = useMemo(() => {
    return forms.filter(
      (form) =>
        (form.type === FormType.SPEAK_UP && flags.speakUpEnabled) ||
        (form.type === FormType.CONFLICT_OF_INTEREST && flags.coiEnabled),
    );
  }, [forms, flags.speakUpEnabled, flags.coiEnabled]);

  const liveCategoryNameById = useMemo(() => {
    const map = new Map<number, string>();
    const addCategories = (cats: CategoryType[] | null | undefined) => {
      transformSpeakUpCategories(cats).forEach((c) => {
        if (c.name) map.set(c.id, c.name);
      });
    };
    addCategories(
      organisationCategoriesData?.getOrganisationCategories as
        | CategoryType[]
        | null
        | undefined,
    );
    addCategories(
      coiOrganisationCategoriesData?.getOrganisationCategories as
        | CategoryType[]
        | null
        | undefined,
    );
    return map;
  }, [
    organisationCategoriesData?.getOrganisationCategories,
    coiOrganisationCategoriesData?.getOrganisationCategories,
  ]);

  const resolvedForms = useMemo(
    () =>
      formsByEnabledTypes.map((form) => {
        const liveName =
          form.categoryId != null
            ? liveCategoryNameById.get(form.categoryId)
            : undefined;
        return liveName ? { ...form, category: liveName } : form;
      }),
    [formsByEnabledTypes, liveCategoryNameById],
  );

  const categoriesForms = useMemo(
    () =>
      mapQuestionnairesToFormData(
        categoriesQuestionnairesData?.formManagementQuestionnaires ?? [],
      ),
    [categoriesQuestionnairesData],
  );

  const speakUpCategories = useMemo(() => {
    const fromForms = getPublishedSpeakUpForms(categoriesForms);
    const orgCategories = transformSpeakUpCategories(
      organisationCategoriesData?.getOrganisationCategories as
        | CategoryType[]
        | null
        | undefined,
    );
    if (orgCategories.length === 0) {
      return fromForms;
    }
    return fromForms.map((cat) => {
      const orgMatch = orgCategories.find((o) =>
        isSameCategoryName(o.name, cat.name),
      );
      const orgDescription = orgMatch?.description?.trim();
      if (orgDescription) {
        return { ...cat, description: orgDescription };
      }
      return cat;
    });
  }, [categoriesForms, organisationCategoriesData?.getOrganisationCategories]);

  const coiCategories = useMemo(() => {
    return getPublishedCoiForms(categoriesForms);
  }, [categoriesForms]);

  const hasNoForms = !isTableLoading && formsByEnabledTypes.length === 0;

  const handleCreateFormWithType = useCallback(
    (formTypeId: string) => {
      const slug = FORM_TYPE_ID_TO_URL_SLUG[formTypeId];
      if (slug == null || slug === '') {
        handleErrorToast({
          toastMessage: 'Unable to open form creation',
          toastDescription: 'The selected form type is not supported.',
        });
        return;
      }
      // Determine return URL based on the form type being created, not current tab
      const formType = formTypeId === 'speak-up' ? 'SPEAK_UP' : 'COI';
      const returnTo = getFormManagementUrlWithTab(formType);
      navigate(`/form-creation/${slug}`, {
        state: { returnTo },
      });
    },
    [navigate],
  );

  const handleOpenCreateFormModal = useCallback(() => {
    if (flags.directFormTypeSlug) {
      // Derive form type from slug for correct return URL
      const formType =
        flags.directFormTypeSlug === 'speak-up' ? 'SPEAK_UP' : 'COI';
      const returnTo = getFormManagementUrlWithTab(formType);
      navigate(`/form-creation/${flags.directFormTypeSlug}`, {
        state: { returnTo },
      });
      return;
    }
    if (flags.showCreateFormModal) {
      handleCreateFormWithType(
        selectedProductTab === 'SPEAK_UP' ? 'speak-up' : 'disclosure',
      );
    }
  }, [
    flags.directFormTypeSlug,
    flags.showCreateFormModal,
    handleCreateFormWithType,
    navigate,
    selectedProductTab,
  ]);

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(currentPageUpdated(page));
    },
    [dispatch],
  );

  const handleItemsPerPageChange = useCallback(
    (option: { value: number; label: string }) => {
      dispatch(itemsPerPageUpdated(option.value));
    },
    [dispatch],
  );

  const handleResetFilters = useCallback(() => {
    dispatch(filtersReset());
  }, [dispatch]);

  const auditLogEnabledTabs = useMemo(
    () => [
      ...(flags.speakUpEnabled ? (['Speak Up'] as const) : []),
      ...(flags.coiEnabled ? (['Conflict of Interest'] as const) : []),
    ],
    [flags.speakUpEnabled, flags.coiEnabled],
  );

  const auditLogCategoryIdMap = useMemo(
    () =>
      buildAuditLogCategoryIdMap(
        questionnairesData?.formManagementQuestionnaires ?? [],
      ),
    [questionnairesData],
  );

  const tableConfig = useMemo(
    () =>
      resolveFormManagementTableConfig(
        flags.speakUpEnabled,
        flags.coiEnabled,
        selectedProductTab === 'COI',
      ),
    [flags.speakUpEnabled, flags.coiEnabled, selectedProductTab],
  );

  const { columns, columnKeys, sortableColumnLabels } = tableConfig;

  const columnWidths = useMemo(
    () => getFormManagementColumnWidths(columnKeys),
    [columnKeys],
  );

  const filteredAndSortedForms = useMemo(() => {
    const productFilteredForms =
      flags.speakUpEnabled && flags.coiEnabled
        ? resolvedForms.filter((form) =>
            selectedProductTab === 'SPEAK_UP'
              ? form.type === FormType.SPEAK_UP
              : form.type === FormType.CONFLICT_OF_INTEREST,
          )
        : resolvedForms;
    const filtered = filterForms(productFilteredForms, {
      searchQuery,
      selectedStatuses,
    });
    const effectiveSortLabel =
      sortColumnLabel != null && sortableColumnLabels.includes(sortColumnLabel)
        ? sortColumnLabel
        : null;
    return sortForms(filtered, effectiveSortLabel, sortDirection);
  }, [
    resolvedForms,
    flags.speakUpEnabled,
    flags.coiEnabled,
    selectedProductTab,
    searchQuery,
    selectedStatuses,
    sortColumnLabel,
    sortDirection,
    sortableColumnLabels,
  ]);

  const showAuditLogInHeader = canCreateForm && formsByEnabledTypes.length > 0;

  const filtersActive = hasActiveFilters(searchQuery, selectedStatuses);
  const numPages = Math.max(
    1,
    Math.ceil(filteredAndSortedForms.length / itemsPerPage),
  );
  const effectivePage = numPages > 0 ? Math.min(currentPage, numPages) : 1;

  useEffect(() => {
    if (currentPage > numPages && numPages >= 1) {
      dispatch(currentPageUpdated(effectivePage));
    }
  }, [currentPage, effectivePage, numPages, dispatch]);

  const paginatedForms = useMemo(
    () =>
      filteredAndSortedForms.slice(
        (effectivePage - 1) * itemsPerPage,
        effectivePage * itemsPerPage,
      ),
    [filteredAndSortedForms, effectivePage, itemsPerPage],
  );

  const handleDuplicateConfirm = useCallback(
    (
      mode:
        | typeof DUPLICATE_MODE.ALL_LANGUAGES
        | typeof DUPLICATE_MODE.DEFAULT_ONLY,
      newTitle: string,
    ) => {
      if (!formToDuplicate) return;
      const slug = getSlugFromFormType(formToDuplicate.type);

      const sourceQuestionnaire =
        questionnairesData?.formManagementQuestionnaires.find(
          (q) => q.id === formToDuplicate.id,
        );

      const sourceLanguages = (sourceQuestionnaire?.languages ?? [])
        .filter((l): l is NonNullable<typeof l> => Boolean(l?.code))
        .map((l) => ({
          label: l.name ?? l.code!,
          value: l.code!,
          isDefault: false,
        }));

      const duplicateFrom: DuplicateFromState = {
        sourceTitle: newTitle,
        displayName: newTitle,
        sourceQuestionnaireId: formToDuplicate.id,
        sourceQuestionnaireStatus: formToDuplicate.status,
        sourceLanguages:
          mode === DUPLICATE_MODE.ALL_LANGUAGES ? sourceLanguages : undefined,
      };
      setFormToDuplicate(null);
      const formType =
        formToDuplicate.type === FormType.SPEAK_UP
          ? FormType.SPEAK_UP
          : FormType.CONFLICT_OF_INTEREST;
      navigate(`/form-creation/${slug}`, {
        state: {
          returnTo: getFormManagementUrlWithTab(formType),
          duplicateFrom,
          duplicateMode: mode,
        },
      });
    },
    [formToDuplicate, navigate, questionnairesData],
  );

  const navigateToFormDetail = useCallback(
    (
      form: Pick<FormData, 'id' | 'type'>,
      returnTo: string = getFormManagementUrlWithTab(form.type),
    ) => {
      const formTypeSlug = getSlugFromFormType(form.type);
      navigate(`/form-management/${formTypeSlug}/form/${form.id}`, {
        state: { returnTo },
      });
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (row: string) => {
      const form = paginatedForms.find((f) => f.id === row);
      if (!form) return;
      navigateToFormDetail(form);
    },
    [paginatedForms, navigateToFormDetail],
  );

  const handleCategoryCardClick = useCallback(
    (category: ActiveCategory) => {
      if (!category.primaryForm) {
        console.warn(
          '[handleCategoryCardClick] Category card has no primaryForm - this should not happen',
          { categoryId: category.id },
        );
        return;
      }
      const returnTo = getFormManagementUrlWithTab(category.primaryForm.type);
      navigateToFormDetail(
        {
          id: category.primaryForm.id,
          type: category.primaryForm.type,
        },
        returnTo,
      );
    },
    [navigateToFormDetail],
  );

  const tableData = useMemo(
    () =>
      buildFormManagementTableData({
        paginatedForms,
        columnKeys,
        navigate,
        formatYmd: formatFormManagementYmd,
        onDuplicate: setFormToDuplicate,
        onArchive: handleArchive,
        onDelete: handleDelete,
        onUnpublish: handleUnpublish,
        onAuditLog: (form) => {
          setAuditLogForm(form);
          setAuditLogOpen(true);
        },
      }),
    [
      columnKeys,
      paginatedForms,
      navigate,
      handleArchive,
      handleDelete,
      handleUnpublish,
    ],
  );

  const tableKey = `table-${columnKeys.join(
    '-',
  )}-${itemsPerPage}-${effectivePage}-${selectedTab}-${searchQuery}-${selectedStatuses.join(
    ',',
  )}-${sortColumnLabel ?? ''}-${sortDirection}`;

  const handleColumnSort = useCallback(
    (columnName: string, direction: 'asc' | 'desc') => {
      dispatch(sortUpdated({ columnLabel: columnName, direction }));
    },
    [dispatch],
  );

  const sortedColumn =
    sortColumnLabel == null || !sortableColumnLabels.includes(sortColumnLabel)
      ? undefined
      : { columnName: sortColumnLabel, direction: sortDirection };

  const showNoCategoriesEmpty =
    !isCategoriesUnsettledForEnabledProducts &&
    !hasCategoriesForAnyEnabledProduct &&
    hasNoForms;

  const showNoFormsYetWithCategories =
    !isCategoriesUnsettledForEnabledProducts &&
    hasCategoriesForAnyEnabledProduct &&
    hasNoForms;

  return {
    flags,
    selectedProductTab,
    setSelectedProductTab,
    setNoCategoriesBannerDismissed,
    auditLogOpen,
    setAuditLogOpen,
    auditLogForm,
    setAuditLogForm,
    formToDuplicate,
    setFormToDuplicate,
    formToArchive,
    setFormToArchive,
    formToUnpublish,
    setFormToUnpublish,
    formToDelete,
    setFormToDelete,
    isError: hasPageError,
    refetch: refetchPageData,
    showNoFormsYetWithCategories,
    showNoCategoriesEmpty,
    showNoCategoriesBanner,
    showAuditLogInHeader,
    canCreateForm,
    createFormDisabledDueToCategories,
    showCreateFormDropdown,
    handleOpenCreateFormModal,
    handleCreateFormWithType,
    speakUpCategories,
    coiCategories,
    isCategoriesLoading,
    categoriesForms,
    handleCategoryCardClick,
    selectedTab,
    searchQuery,
    filtersActive,
    handleResetFilters,
    selectedStatuses,
    isTableLoading,
    tableKey,
    columns,
    columnWidths,
    sortableColumnLabels,
    tableData,
    effectivePage,
    handlePageChange,
    sortedColumn,
    handleColumnSort,
    itemsPerPage,
    handleItemsPerPageChange,
    totalCount: filteredAndSortedForms.length,
    filteredAndSortedForms,
    formsByEnabledTypes,
    handleRowClick,
    handleDuplicateConfirm,
    handleArchiveConfirm,
    handleUnpublishConfirm,
    handleDeleteConfirm,
    deleteMutationLoading,
    unpublishMutationLoading,
    auditLogEnabledTabs,
    auditLogCategoryIdMap,
  };
}
